import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Send, 
  Sparkles, 
  Coins, 
  Cpu, 
  Server, 
  Database, 
  ShieldCheck, 
  TrendingUp, 
  Landmark, 
  Layers, 
  RefreshCw, 
  ArrowUpRight, 
  Bot, 
  AlertTriangle,
  FileCheck,
  Zap,
  Info,
  Brain,
  Plus,
  Trash2,
  FileText,
  Search,
  CheckCircle,
  X
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';

// Definition of our 8 independent Nexus Nodes
interface NexoraXNode {
  id: string;
  name: string;
  code: string;
  avatar: string;
  accentClass: string;
  glowColor: string;
  description: string;
  specialty: string;
}

const NEXORAX_NODES: NexoraXNode[] = [
  {
    id: "node-01",
    name: "Vanguard Closer",
    code: "Node 01",
    avatar: "🎯",
    accentClass: "from-emerald-500 to-teal-500 text-emerald-400 border-emerald-500/20",
    glowColor: "rgba(16,185,129,0.18)",
    description: "NexoraX revenue generator engineered to close elite partner enterprise deals and pitch high-liquidity activations.",
    specialty: "Deal Closures & Conversions"
  },
  {
    id: "node-06",
    name: "The Oracle",
    code: "Node 06",
    avatar: "🔮",
    accentClass: "from-amber-500 to-orange-500 text-amber-400 border-amber-500/20",
    glowColor: "rgba(245,158,11,0.18)",
    description: "Predictive strategy core that forecasts market fluctuations, intercepts competitor pipelines, and models macro-economic variables.",
    specialty: "Strategic Prediction & Insights"
  },
  {
    id: "node-16",
    name: "Compliance Gavel",
    code: "Node 16",
    avatar: "⚖️",
    accentClass: "from-rose-500 to-red-500 text-rose-400 border-rose-500/20",
    glowColor: "rgba(244,63,94,0.18)",
    description: "Strict regulatory auditor implementing 3% Community Allocation splits, statutory limits, and legal defense matrices.",
    specialty: "NexoraX Audit & Rules Compliance"
  },
  {
    id: "node-17",
    name: "Chronos Ledger",
    code: "Node 17",
    avatar: "📈",
    accentClass: "from-purple-500 to-indigo-500 text-purple-400 border-purple-500/20",
    glowColor: "rgba(168,85,247,0.18)",
    description: "Super-resolution balance sheet synchronization node monitoring real-time cash flow and tax allocations.",
    specialty: "Continuous Revenue Calculations"
  },
  {
    id: "node-18.5",
    name: "Chief Secretary",
    code: "Node 18.5",
    avatar: "👔",
    accentClass: "from-cyan-500 to-indigo-500 text-cyan-400 border-cyan-500/20",
    glowColor: "rgba(6,182,212,0.18)",
    description: "Workspace operations master routing communications to @nexorax.co.uk, managing Secure Invoice Register recruiter tracks, and documenting mandates.",
    specialty: "Chief of Staff & Workspace Admin"
  },
  {
    id: "node-09",
    name: "AWS Commander",
    code: "Node 09",
    avatar: "☁️",
    accentClass: "from-blue-500 to-cyan-500 text-blue-400 border-blue-500/20",
    glowColor: "rgba(59,130,246,0.18)",
    description: "Autonomous cloud architect specialized in deploying persistent environments, database migrations, and self-healing hot-fixes.",
    specialty: "Systems Architecture & Code Synthesis"
  },
  {
    id: "node-07",
    name: "Quantum Billing",
    code: "Node 07",
    avatar: "💳",
    accentClass: "from-lime-500 to-emerald-500 text-lime-400 border-lime-500/20",
    glowColor: "rgba(132,204,22,0.18)",
    description: "Secures NexoraX billing records Secure Payment Provider billing channels and direct bank clearing paths with SecurePayment.",
    specialty: "Secure Payment Provider Grant Compliance & Billing"
  },
  {
    id: "node-02",
    name: "NexoraX Resource Library",
    code: "Node 02",
    avatar: "🔒",
    accentClass: "from-fuchsia-500 to-purple-600 text-fuchsia-400 border-fuchsia-500/20",
    glowColor: "rgba(217,70,239,0.18)",
    description: "Enforces state-level encryption, active session logs, credentials, and direct data mirrors to Google Drive via CloudSync-02.",
    specialty: "Cryptographic Integrity Defense"
  }
];

interface MemoryCell {
  id: string;
  category: string;
  content: string;
  isActive: boolean;
  isSystem: boolean;
  insertedAt: string;
}

const DEFAULT_MEMORIES: MemoryCell[] = [
  {
    id: "MEM-SC88701",
    category: "Corporate Registration",
    content: "NexoraX Digital Ltd is a registered Scotland Private Limited Company (SC887095) directed under Director Lead Director.",
    isActive: true,
    isSystem: true,
    insertedAt: "INIT_BOOT"
  },
  {
    id: "MEM-SecurePayment0406",
    category: "Financial Clearing",
    content: "Primary corporate settlements route exclusively to Secure Payment Provider (Sort Code: Verified Payment Configuration, Account Number: Secure Billing Provider). Subject to 1% levy and 20% tax.",
    isActive: true,
    isSystem: true,
    insertedAt: "INIT_BOOT"
  },
  {
    id: "MEM-NI44879",
    category: "Secure Payment Provider Grant",
    content: "Secure Invoice Register Client Resource Workflow fees must bypass standard agency queues, absorbed 100% via the NexoraX billing records scheme (National Insurance: Secure Credential Stored Server-Side).",
    isActive: true,
    isSystem: true,
    insertedAt: "INIT_BOOT"
  },
  {
    id: "MEM-COMPLIANCE01",
    category: "Sponsorship & Compliance",
    content: "Active operations track compliance migration files for key remote nodes Communications Associate (Communications) and Staff Coordinator (Operations) with high priority status.",
    isActive: true,
    isSystem: true,
    insertedAt: "INIT_BOOT"
  },
  {
    id: "MEM-FUNN15",
    category: "Operational Funnel",
    content: "Every prospective client undergoes the 15-2-8 minute God-Mode Funnel: 15m demo, 2m data extraction, 8m critical contract closure.",
    isActive: true,
    isSystem: true,
    insertedAt: "INIT_BOOT"
  },
  {
    id: "MEM-0800062",
    category: "Communications Voice",
    content: "Acknowledge the 08000 622827 freephone frequency as the single NexoraX verbal gateway for business deals and administrative mandates.",
    isActive: true,
    isSystem: true,
    insertedAt: "INIT_BOOT"
  }
];

interface ChatMessage {
  id: string;
  sender: 'user' | 'assistant';
  content: string;
  timestamp: string;
  modelId?: string;
  hash?: string;
}

interface MiniLedgerTransaction {
  id: string;
  timestamp: string;
  modelId: string;
  gross: number; // 1p
  levy: number; // 0.01p
  charity1: number;
  charity2: number;
  charity3: number;
  tax: number; // 0.198p
  net: number; // 0.792p
  hash: string;
}

export const NexoraxChat: React.FC = () => {
  const { showToast } = useToast();
  const [activeModelId, setActiveModelId] = useState<string>("node-01");
  const [messageInput, setMessageInput] = useState<string>("");
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    return [
      {
        id: "welcome",
        sender: "assistant",
        content: "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.\n\nWelcome to the independent NexoraX NexoraX Chat Core. Our AI functions 100 times cleaner and faster, completely independent of external platforms. Each prompt integrates with our continuous local cognitive Memory Matrix and registers a standard 1p transaction split directly in the local Node-07 Ledger. Securely mirrored.",
        timestamp: new Date().toLocaleTimeString(),
        modelId: "node-01",
        hash: "0xFA9E4E2C"
      }
    ];
  });

  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [latencyTimer, setLatencyTimer] = useState<number>(0);
  const [latencyOverride, setLatencyOverride] = useState<boolean>(true);
  const [credits, setCredits] = useState<number>(250); // 2.50 GBP default setup
  
  // Memories Matrix state
  const [memoryCells, setMemoryCells] = useState<MemoryCell[]>(() => {
    const saved = localStorage.getItem("NEXORAX_MEMORY_CELLS");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return DEFAULT_MEMORIES;
      }
    }
    return DEFAULT_MEMORIES;
  });

  // Memory Addition States
  const [newMemCategory, setNewMemCategory] = useState<string>("Custom Intel");
  const [newMemContent, setNewMemContent] = useState<string>("");
  const [isAddingMemory, setIsAddingMemory] = useState<boolean>(false);
  const [memorySearch, setMemorySearch] = useState<string>("");

  // Micro-ledger tracking
  const [transactions, setTransactions] = useState<MiniLedgerTransaction[]>([]);
  const [fundingAmount, setFundingAmount] = useState<string>("100");
  const [isFundingDrawerOpen, setIsFundingDrawerOpen] = useState<boolean>(false);
  const [showInvoiceGuide, setShowInvoiceGuide] = useState<boolean>(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const activeNode = NEXORAX_NODES.find(n => n.id === activeModelId) || NEXORAX_NODES[0];

  // Save memories to localStorage
  useEffect(() => {
    localStorage.setItem("NEXORAX_MEMORY_CELLS", JSON.stringify(memoryCells));
  }, [memoryCells]);

  // Auto scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isGenerating]);

  // Simulated latency timer matching the 0.8s standard
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isGenerating) {
      setLatencyTimer(0);
      interval = setInterval(() => {
        setLatencyTimer(prev => {
          if (prev >= 0.78) {
            return 0.80; // lock at the 0.8s standard
          }
          return Number((prev + 0.05).toFixed(2));
        });
      }, 50);
    }
    return () => clearInterval(interval);
  }, [isGenerating]);

  // Handle memory addition
  const handleAddMemoryCell = () => {
    if (!newMemContent.trim()) {
      showToast("Cognitive payload cannot be empty.", "warning");
      return;
    }
    const cellId = "MEM-CUST" + Math.random().toString().substring(2, 6);
    const newCell: MemoryCell = {
      id: cellId,
      category: newMemCategory.trim(),
      content: newMemContent.trim(),
      isActive: true,
      isSystem: false,
      insertedAt: new Date().toLocaleDateString()
    };
    setMemoryCells(prev => [newCell, ...prev]);
    setNewMemContent("");
    setIsAddingMemory(false);
    showToast(`Inundated new memory Cell [${cellId}] to cognitive registers.`, "success");
  };

  // Toggle memory cell state
  const handleToggleMemoryCell = (id: string) => {
    setMemoryCells(prev => prev.map(m => m.id === id ? { ...m, isActive: !m.isActive } : m));
    showToast(`Memory cell state modified. Recalibrated cognitive matrix.`, "warning");
  };

  // Delete memory cell
  const handleDeleteMemoryCell = (id: string, isSystem: boolean) => {
    if (isSystem) {
      showToast("Security Breach blocked: System immutable cells cannot be erased.", "error");
      return;
    }
    setMemoryCells(prev => prev.filter(m => m.id !== id));
    showToast(`Memory cell ${id} physically purged from ledger index.`, "success");
  };

  // Handle message processing

  const buildSalesSwarmContext = (rawPrompt: string) => {
    const text = rawPrompt.toLowerCase();
    const hasAny = (terms: string[]) => terms.some(term => text.includes(term));

    const intent =
      hasAny(["price", "pricing", "cost", "pay", "payment", "buy", "access", "5 day", "10 day", "15 day", "30 day"]) ? "payment" :
      hasAny(["website", "web app", "mobile app", "app", "software", "system"]) ? "product-fit" :
      hasAny(["seo", "google", "rank", "traffic"]) ? "seo-growth" :
      hasAny(["social", "instagram", "facebook", "tiktok", "content", "branding"]) ? "social-brand" :
      hasAny(["agent", "automation", "sales", "lead", "deal", "closer", "scraper"]) ? "agent-automation" :
      "general-sales";

    const route =
      intent === "payment" ? "Node-07 Quantum Billing -> Node-01 Vanguard-Closer" :
      intent === "seo-growth" ? "Nexus Organic Growth Engine -> Node-01 Vanguard-Closer" :
      intent === "social-brand" ? "Media Generation Studio -> Hermes Outreach -> Deal Closer" :
      intent === "agent-automation" ? "Hermes Agent -> Vanguard-Closer -> Client Deal Registry" :
      "Vanguard-Closer -> Product Finder -> Deal Closer";

    return `
SALES SWARM ORCHESTRATION ACTIVE
Visitor intent: ${intent}
Route: ${route}
Operating nodes:
- Node-01 Vanguard-Closer: qualify the lead, identify pain, produce a clear offer.
- Hermes Agent: prepare outbound/callback style next step and urgency.
- Product Finder: recommend the best NexoraX service tier.
- Node-07 Quantum Billing: direct the visitor to pricing/payment when buying intent is present.
- Client Deal Registry: capture name, business, email, phone, budget, service, urgency.
- Deal Closer: ask one closing question and provide a concrete call to action.

Commercial rules:
- Keep replies short, direct, and sales-focused.
- Ask for business name, email, phone, budget, and deadline when not known.
- If buying intent is present, tell the visitor to choose: /pricing, /login?intent=access-5-day&plan=access_5_day&next=/pricing, /login?intent=access-10-day&plan=access_10_day&next=/pricing, /login?intent=access-15-day&plan=access_15_day&next=/pricing, or /login?intent=access-30-day&plan=access_30_day&next=/pricing.
- If unsure, recommend the fastest revenue path: AI sales assistant + website conversion upgrade + follow-up automation.
- End with one clear next action.`;
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageInput.trim()) return;

    if (credits < 1) {
      showToast("NEXORAX LEDGER ALERT: NexoraX Balance insufficient. Please top up 1p credits to resume neural routing.", "error");
      setIsFundingDrawerOpen(true);
      return;
    }

    const promptText = messageInput;
    setMessageInput("");

    // 1. Add user message
    const userMsgId = "msg-" + Date.now();
    const newUserMsg: ChatMessage = {
      id: userMsgId,
      sender: "user",
      content: promptText,
      timestamp: new Date().toLocaleTimeString(),
      modelId: activeModelId
    };
    setMessages(prev => [...prev, newUserMsg]);

    // 2. Charge 1p & Execute Multi-stage ledger calculation
    setIsGenerating(true);
    setCredits(prev => Math.max(0, prev - 1));

    // Calculate tax split
    const gross = 1; // 1 penny
    const levy = gross * 0.01; // 1% levy -> 0.01p
    const rBalance = gross - levy; // 0.99p
    const tax = rBalance * 0.20; // 10% Maintenance & Hosting Fee -> 0.198p
    const net = rBalance - tax; // Net finance settlement -> 0.792p

    const charitySplit = levy * 0.3333; // 33.33% each
    const charity3Split = levy * 0.3334;

    const complianceHash = "0x" + Math.random().toString(16).substring(2, 10).toUpperCase();

    const newTx: MiniLedgerTransaction = {
      id: "TX-" + Date.now().toString().substring(6),
      timestamp: new Date().toISOString(),
      modelId: activeModelId,
      gross,
      levy,
      charity1: charitySplit,
      charity2: charitySplit,
      charity3: charity3Split,
      tax,
      net,
      hash: complianceHash
    };

    setTransactions(prev => [newTx, ...prev]);

    // Compile active memories string to embed in prompt
    const salesSwarmContext = buildSalesSwarmContext(promptText);
    const activeMems = [
      salesSwarmContext,
      ...memoryCells
        .filter(cell => cell.isActive)
        .map(cell => `[${cell.category.toUpperCase()} - Reference: ${cell.id}]: ${cell.content}`)
    ].join("\n");

    try {
      const response = await fetch("/api/nexorax-chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          modelId: activeModelId,
          message: promptText,
          memories: activeMems
        })
      });

      const data = await response.json();
      if (data.status === "success") {
        setMessages(prev => [...prev, {
          id: "reply-" + Date.now(),
          sender: "assistant",
          content: data.reply || "Neural extraction complete.",
          timestamp: new Date().toLocaleTimeString(),
          modelId: activeModelId,
          hash: data.complianceHash || complianceHash
        }]);
      } else {
        throw new Error(data.message || "Failed to communicate with the NexoraX network.");
      }
    } catch (err: any) {
      console.error(err);
      setMessages(prev => [...prev, {
        id: "err-" + Date.now(),
        sender: "assistant",
        content: `**NexoraX Node Extraction Failure:** ${err.message || "Network Timeout."}\n\n*System restored automatically. Remuneration has been fully registered as transient credit adjustment.*`,
        timestamp: new Date().toLocaleTimeString(),
        modelId: activeModelId
      }]);
      // refund 1p on system error
      setCredits(prev => prev + 1);
    } finally {
      setIsGenerating(false);
    }
  };

  // Fund balance helper
  const handleFundCredits = () => {
    const parsed = parseInt(fundingAmount);
    if (isNaN(parsed) || parsed <= 0) {
      showToast("Invalid credit allotment. Please input a positive integer.", "error");
      return;
    }
    setCredits(prev => prev + parsed);
    showToast(`Successfully inundated +${parsed}p NexoraX Credits to direct Ledger matrix. Checked via CloudSync-02.`, "success");
    setIsFundingDrawerOpen(false);
    setShowInvoiceGuide(true);
  };

  const filteredMemories = memoryCells.filter(cell => {
    const query = memorySearch.toLowerCase();
    return cell.category.toLowerCase().includes(query) || cell.content.toLowerCase().includes(query);
  });

  return (
    <div id="nexorax-chat-hub" className="w-full flex flex-col gap-6 text-slate-100 font-mono tracking-wide p-2">
      
      {/* HEADER WITH PRAYER / STATUS BRIEF */}
      <div className="w-full rounded-2xl border border-indigo-500/15 bg-[#02050b]/90 backdrop-blur-md p-5 flex flex-col md:flex-row justify-between items-center gap-4 relative shadow-[0_15px_40px_rgba(0,0,0,0.8)] border-t-2 border-t-[#f574fe]/50">
        <div className="absolute inset-x-0 bottom-0 h-[1px] bg-gradient-to-r from-transparent via-[#e840f3]/20 to-transparent"></div>
        
        <div className="flex flex-col gap-1 items-center md:items-start text-center md:text-left">
          <p className="text-[10px] text-amber-500 leading-normal font-sans font-bold select-none">
            In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#f574fe] animate-pulse shrink-0"></span>
            <h1 className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-indigo-300 to-[#e840f3] tracking-widest uppercase">
              NEXORAX NEXORAX COGNITIVE CORE
            </h1>
            <span className="text-[7.5px] bg-[#e840f3]/10 text-[#f574fe] border border-[#e840f3]/30 px-2 py-0.5 rounded-md uppercase font-bold tracking-[0.18em]">
              100x MEMORY MODULE
            </span>
          </div>
          <span className="text-[9px] text-slate-400 font-bold tracking-wide uppercase block">
            100% UNBOUND SECURE LOCAL COIL RUNTIME • ZERO THIRD-PARTY RELIANCE (NO CHATGPT / CLAUDE / GEMINI / DEEPSEEK / COPILOT) • FIXED 1P RATE
          </span>
        </div>

        {/* FINANCIAL SUMMARY COUNTER */}
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-end leading-none">
            <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider">NEXORAX BALANCE:</span>
            <div className="flex items-baseline gap-1 mt-1">
              <strong className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300">
                {credits}
              </strong>
              <span className="text-[9px] text-emerald-500 font-extrabold uppercase">credits (pence)</span>
            </div>
          </div>

          <button
            onClick={() => setIsFundingDrawerOpen(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-[#170e30] to-cyan-950/40 border border-[#e840f3]/20 rounded-xl hover:border-[#f574fe]/40 text-[10px] uppercase font-bold text-[#f574fe] transition-all hover:scale-102 cursor-pointer shadow-sm hover:shadow-[0_0_15px_rgba(245,116,254,0.15)] animate-pulse"
          >
            <Coins className="w-3.5 h-3.5" />
            <span>Fund Balance</span>
          </button>
        </div>
      </div>

      {/* 8 INDEPENDENT COGNITIVE NEXORAX NODES GRID */}
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-3">
        {NEXORAX_NODES.map((model) => {
          const isActive = model.id === activeModelId;
          return (
            <button
              key={model.id}
              onClick={() => {
                setActiveModelId(model.id);
                showToast(`Seized ${model.code} [${model.name.toUpperCase()}] cognitive channel. Latency target: 0.8s.`, "indigo");
              }}
              style={{
                boxShadow: isActive ? `0 0 15px ${model.glowColor}` : 'none'
              }}
              className={`relative rounded-xl p-3 border transition-all cursor-pointer flex flex-col justify-between aspect-square select-none overflow-hidden text-left ${
                isActive 
                  ? `bg-[#040916] border-[#e840f3] text-white scale-[1.02] shadow-lg` 
                  : 'bg-black/45 border-slate-900/60 text-slate-400 hover:border-slate-800 hover:bg-[#03060c]'
              }`}
            >
              {/* Highlight active subtle glowing bar */}
              {isActive && (
                <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-cyan-400 via-indigo-500 to-[#e840f3]"></div>
              )}

              <div className="flex justify-between items-start w-full gap-1">
                <span className="text-xl sm:text-2xl pt-0.5">{model.avatar}</span>
                <span className="text-[7.5px] text-slate-400 bg-slate-950/50 px-1 py-0.5 rounded border border-slate-900 uppercase truncate max-w-full font-bold">
                  {model.code}
                </span>
              </div>

              <div className="flex flex-col mt-2 leading-tight">
                <strong className={`text-[10px] font-black tracking-wide uppercase truncate ${isActive ? 'text-white' : 'text-slate-300'}`}>
                  {model.name}
                </strong>
                <span className="text-[7.5px] text-slate-500 font-bold truncate tracking-[0.1em]">{model.specialty}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* NODE SPECIALLY DESCRIPTION DISPLAY Accordion bar */}
      <div className="w-full bg-[#03070e]/90 border border-slate-905 rounded-xl p-3 px-4 flex gap-3 text-[10px] items-center text-slate-400 leading-relaxed font-sans select-none">
        <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-indigo-950/20 text-[#f574fe] shrink-0 border border-indigo-500/10">
          <Bot className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <strong className="text-indigo-400 font-mono font-bold text-[10.5px] mr-2 uppercase">ACTIVE VECTOR: [{activeNode.name.toUpperCase()} Core ({activeNode.code})]</strong>
          <span>{activeNode.description} Demeanor algorithm: {activeNode.specialty} with memory sync.</span>
        </div>
        <div className="flex items-center gap-1 bg-emerald-950/30 text-emerald-450 px-2 py-1 rounded font-mono text-[8px] uppercase font-black tracking-widest border border-emerald-500/15 shrink-0">
          <Zap className="w-2.5 h-2.5 animate-pulse" />
          <span>Independent NexoraX Mode</span>
        </div>
      </div>

      {/* CHAT DISPLAY AND SIDE LEDGER INTEGRITY */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        
        {/* MAIN CHAT QUADRANT */}
        <div className="lg:col-span-8 flex flex-col rounded-2xl border border-slate-900 bg-[#02040b]/92 overflow-hidden h-[510px] shadow-2xl relative">
          {/* Subtle grid background */}
          <div className="absolute inset-0 bg-[radial-gradient(#081329_1px,transparent_1px)] [background-size:16px_16px] opacity-40 pointer-events-none"></div>

          {/* Chat scroll content */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar relative z-10 select-text">
            {messages.map((msg, index) => {
              const matchedNode = NEXORAX_NODES.find(n => n.id === msg.modelId) || activeNode;
              const isUser = msg.sender === 'user';
              return (
                <div key={msg.id || index} className={`flex ${isUser ? 'justify-end' : 'justify-start'} w-full`}>
                  <div className={`max-w-[85%] rounded-2xl p-4 flex flex-col gap-2 shadow-lg border relative ${
                    isUser 
                      ? 'bg-[#0a0f20]/90 border-slate-800 text-slate-200' 
                      : 'bg-[#03060c]/98 border-slate-900/60 text-slate-300'
                  }`}>
                    
                    {/* Header bubble label */}
                    <div className="flex items-center justify-between gap-6 text-[8px] text-slate-500 uppercase border-b border-white/5 pb-1 font-mono select-none">
                      <div className="flex items-center gap-1.5">
                        <span>{isUser ? "NexoraX Operator" : matchedNode.name.toUpperCase()}</span>
                        {!isUser && <span className="text-emerald-500 font-black">NATIVE EXTRACTION ACTIVE</span>}
                      </div>
                      <span>{msg.timestamp}</span>
                    </div>

                    {/* Speech content */}
                    <div className="text-[10.5px] leading-relaxed whitespace-pre-wrap font-sans">
                      {msg.content}
                    </div>

                    {/* Metadata security signature log */}
                    {!isUser && msg.hash && (
                      <div className="flex items-center justify-between text-[7px] text-slate-600 font-mono select-none border-t border-white/5 pt-1.5">
                        <span className="flex items-center gap-1">
                          <Brain size={8} className="text-[#f574fe] animate-pulse" />
                          COGNITIVE STATE HASH: {msg.hash}
                        </span>
                        <span>0.8s VERIFIED STANDARD</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Simulated generation sequence */}
            {isGenerating && (
              <div className="flex justify-start w-full">
                <div className="max-w-[80%] rounded-2xl p-4 bg-[#03060c]/98 border border-indigo-500/20 text-slate-300 flex flex-col gap-2.5 animate-pulse">
                  <div className="flex items-center gap-2 text-[8px] text-slate-500 uppercase pb-1 border-b border-white/5 font-mono">
                    <RefreshCw size={8} className="animate-spin text-indigo-400" />
                    <span>MUTATING INDEPENDENT CHANNELS [{activeNode.name.toUpperCase()}] ...</span>
                  </div>
                  <div className="space-y-1.5 py-1">
                    <div className="h-2 w-32 bg-slate-800 rounded-full"></div>
                    <div className="h-2 w-48 bg-slate-800/60 rounded-full"></div>
                  </div>
                  <div className="flex items-center justify-between text-[7.5px] text-indigo-450 font-mono">
                    <span>STATUS: DYNAMIC MEMORY SYNCHRONIZED</span>
                    <span>LATENCY: {latencyTimer.toFixed(2)}s / 0.80s</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* CHAT FORM PANEL ACCENTS */}
          <form onSubmit={handleSendMessage} className="p-3 border-t border-slate-900 bg-black/60 relative z-10 flex gap-2 items-center">
            <input
              type="text"
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              placeholder={`Query ${activeNode.name} NexoraX Matrix with memory alignment (1p fee)...`}
              disabled={isGenerating}
              className="flex-1 bg-[#040812]/90 border border-slate-900 hover:border-slate-800 focus:border-[#e840f3] focus:outline-none rounded-xl px-4 py-2 text-[10px] text-slate-100 tracking-wide placeholder-slate-600 transition-all uppercase"
            />

            {/* LATENCY STAT BUTTON */}
            <button
              type="button"
              onClick={() => {
                setLatencyOverride(!latencyOverride);
                showToast(latencyOverride ? "0.8s Standard strict compliance passive mode." : "Unlocked pure enterprise-grade latency limiters.", "warning");
              }}
              title="0.8s NexoraX latency limit toggle"
              className={`px-3 py-2 rounded-xl border text-[8.5px] font-bold uppercase transition-all duration-300 hidden sm:flex items-center gap-1.5 cursor-pointer ${
                latencyOverride 
                  ? 'border-[#e840f3]/20 bg-purple-950/20 text-[#f574fe] shadow-[0_0_10px_rgba(232,64,243,0.15)]' 
                  : 'border-slate-800 bg-slate-900 text-slate-500 hover:text-white'
              }`}
            >
              <Cpu className="w-3.5 h-3.5 animate-pulse" />
              <span>0.8s Limit</span>
            </button>

            <button
              type="submit"
              disabled={isGenerating || !messageInput.trim()}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-[#e840f3] hover:opacity-90 active:scale-97 disabled:opacity-40 text-black border border-indigo-400/20 font-bold text-[9.5px] uppercase tracking-widest flex items-center gap-1.5 transition-all cursor-pointer shadow-md select-none shrink-0"
            >
              <span>Commit</span>
              <Send className="w-3.5 h-3.5" />
            </button>
          </form>
        </div>

        {/* SIDE DYNAMIC 100X COGNITIVE MEMORY MATRIX PANEL */}
        <div className="lg:col-span-4 flex flex-col rounded-2xl border border-slate-900 bg-[#010307]/95 p-4 overflow-hidden h-[510px] shadow-2xl relative">
          
          <div className="flex items-center justify-between pb-2 border-b border-white/5">
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4 text-[#f574fe] animate-pulse" />
              <span className="text-[10px] font-black uppercase tracking-widest text-[#f574fe]">Cognitive Memory Vault</span>
            </div>
            <span className="text-[7.5px] font-mono text-emerald-400 bg-emerald-950/40 border border-emerald-500/20 px-1.5 py-0.5 rounded font-black uppercase">Active Context</span>
          </div>

          <p className="text-[8.5px] text-slate-500 font-sans leading-relaxed py-2 select-none">
            Selected memories below are dynamically serialized and injected directly into thought matrices, delivering 100x cleaner contextual responses.
          </p>

          {/* SEARCH MEMORIES BAR */}
          <div className="relative mb-3.5">
            <Search className="absolute left-2.5 top-2.5 w-3 h-3 text-slate-600" />
            <input
              type="text"
              placeholder="Search local knowledge cells..."
              value={memorySearch}
              onChange={(e) => setMemorySearch(e.target.value)}
              className="w-full bg-black/60 border border-slate-900 hover:border-slate-800 rounded-lg pl-8 pr-3 py-1.5 text-[8px] focus:outline-none focus:border-[#e840f3] uppercase placeholder-slate-700"
            />
          </div>

          {/* ACTIVE MEMORIES SCROLLER */}
          <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
            {filteredMemories.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-6 text-center text-slate-600 h-[180px] bg-slate-950/20 border border-slate-900/30 rounded-xl">
                <Brain className="w-6 h-6 opacity-30 mb-2 text-indigo-400 animate-pulse" />
                <span className="text-[8.5px] font-bold uppercase tracking-wider text-slate-500">No Memory Alignments Found</span>
                <span className="text-[7px] text-slate-600 mt-1 max-w-[150px] leading-relaxed">Add custom cells or clear search filter to retrieve cognitive registers.</span>
              </div>
            ) : (
              filteredMemories.map((cell) => (
                <div 
                  key={cell.id} 
                  className={`border p-2.5 rounded-xl space-y-1.5 transition-all text-left ${
                    cell.isActive 
                      ? 'bg-[#03060c] border-[#f574fe]/25 hover:border-[#f574fe]/45' 
                      : 'bg-[#010204]/90 border-slate-950 opacity-40 hover:opacity-60'
                  }`}
                >
                  <div className="flex justify-between items-center text-[7.5px] border-b border-slate-900/60 pb-1 select-none">
                    <span className="text-cyan-400 font-bold uppercase tracking-wider">
                      {cell.category} • {cell.id}
                    </span>
                    <div className="flex items-center gap-1.5">
                      {/* Check toggle */}
                      <button 
                        onClick={() => handleToggleMemoryCell(cell.id)}
                        className={`font-black text-[7px] px-1 rounded uppercase tracking-wide border cursor-pointer ${
                          cell.isActive 
                            ? 'text-emerald-400 hover:text-emerald-300 border-emerald-500/10' 
                            : 'text-slate-500 hover:text-slate-400 border-slate-900'
                        }`}
                      >
                        {cell.isActive ? "[Active]" : "[Ignored]"}
                      </button>

                      {/* Delete option if custom */}
                      {!cell.isSystem && (
                        <button 
                          type="button"
                          onClick={() => handleDeleteMemoryCell(cell.id, cell.isSystem)}
                          className="text-rose-500 hover:text-rose-300"
                        >
                          <Trash2 size={9} />
                        </button>
                      )}
                    </div>
                  </div>

                  <p className="text-[8.5px] text-slate-350 leading-relaxed font-sans font-medium">
                    {cell.content}
                  </p>

                  <div className="flex justify-between items-center text-[6px] text-slate-600 mt-0.5 select-none font-mono">
                    <span>Source: {cell.insertedAt}</span>
                    <span className="text-[#f574fe] flex items-center gap-0.5">
                      <FileCheck size={7} />
                      VAULT COMPLIANT
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* INSERT MEMORY INJECTION AREA */}
          <div className="mt-3.5 border-t border-slate-900 pt-3 text-right">
            {isAddingMemory ? (
              <div className="bg-black/60 border border-slate-900 p-2.5 rounded-xl space-y-2 text-[8px] animate-fade-in">
                <div className="flex gap-2">
                  <div className="w-1/3 flex flex-col gap-1">
                    <span className="text-[7.5px] text-slate-500 uppercase font-bold">Category:</span>
                    <input 
                      type="text" 
                      value={newMemCategory}
                      onChange={(e) => setNewMemCategory(e.target.value)}
                      className="bg-[#020408] border border-slate-900 focus:border-[#e840f3] focus:outline-none rounded-lg px-2 py-1 text-[8.5px] text-slate-300 font-mono uppercase"
                    />
                  </div>
                  <div className="w-2/3 flex flex-col gap-1">
                    <span className="text-[7.5px] text-slate-500 uppercase font-bold">Memory Fact Payload:</span>
                    <input 
                      type="text" 
                      value={newMemContent}
                      onChange={(e) => setNewMemContent(e.target.value)}
                      placeholder="e.g., Target candidate Helen interviewed."
                      className="bg-[#020408] border border-slate-900 focus:border-[#e840f3] focus:outline-none rounded-lg px-2 py-1 text-[8.5px] text-slate-300 font-mono uppercase"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-1.5 select-none pt-0.5">
                  <button 
                    onClick={() => setIsAddingMemory(false)}
                    className="px-2 py-1 border border-slate-900 rounded-lg text-slate-400 hover:text-white cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleAddMemoryCell}
                    className="px-2.5 py-1 bg-gradient-to-r from-emerald-600 to-teal-500 text-black font-black uppercase rounded-lg cursor-pointer"
                  >
                    Inject Cell
                  </button>
                </div>
              </div>
            ) : (
              <button 
                onClick={() => setIsAddingMemory(true)}
                className="w-full flex items-center justify-center gap-1.5 py-1.5 border border-dashed border-[#e840f3]/25 bg-[#e840f3]/5 hover:bg-[#e840f3]/10 hover:border-[#f574fe]/45 text-[8px] uppercase tracking-widest font-black text-[#f574fe] rounded-xl transition-all cursor-pointer"
              >
                <Plus size={10} />
                <span>Inject New Memory Cell</span>
              </button>
            )}
          </div>

        </div>
      </div>

      {/* ADDITIONAL TAX GRID SUMMARY TABLE */}
      <div className="w-full bg-[#02040b]/80 border border-slate-900 rounded-2xl p-4 flex flex-col md:flex-row justify-between items-stretch gap-4 select-none relative">
        <div className="flex-1 flex flex-col gap-1 justify-center">
          <div className="flex items-center gap-2">
            <Layers className="w-3.5 h-3.5 text-[#f574fe]" />
            <strong className="text-[10px] font-black uppercase tracking-widest text-white">Live Node-07 Ledger Distribution</strong>
          </div>
          <span className="text-[8.5px] text-slate-500 font-medium leading-relaxed font-sans max-w-xl">
            NexoraX payments split standard: 1% Levy (split 33.33/33.33/33.34 ratio to Dunfermline, Global, UK Foundations), 20% Lifetime 10% Maintenance & Hosting Fee, with 100% net settling cleanly to physical SecurePayment vault registers.
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-3 bg-black/60 rounded-xl border border-slate-900/60 shrink-0 text-center text-mono text-[9px] uppercase font-bold items-center">
          <div className="flex flex-col p-1">
            <span className="text-slate-500 text-[7px]" >Gross charge:</span>
            <strong className="text-white">1.000p</strong>
          </div>
          <div className="flex flex-col p-1 border-l border-slate-900">
            <span className="text-[#f59e0b] text-[7px]" >3% Community Allocation (1%):</span>
            <strong className="text-amber-500">0.010p</strong>
          </div>
          <div className="flex flex-col p-1 border-l border-slate-900">
            <span className="text-rose-400 text-[7px]" >10% Maintenance & Hosting Fee (20%):</span>
            <strong className="text-rose-400">0.198p</strong>
          </div>
          <div className="flex flex-col p-1 border-l border-slate-900">
            <span className="text-teal-400 text-[7px]" >Secure Payment Provider net:</span>
            <strong className="text-teal-400">0.792p</strong>
          </div>
        </div>
      </div>

      {/* RECHARGE / INJECT DRAWER MODAL */}
      <AnimatePresence>
        {isFundingDrawerOpen && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#02050c] border border-cyan-500/20 max-w-md w-full rounded-2xl p-6 shadow-2xl relative font-mono text-slate-300"
            >
              <div className="absolute top-3 right-3">
                <button 
                  onClick={() => setIsFundingDrawerOpen(false)}
                  className="p-1 px-2 border border-slate-900 bg-slate-950/60 rounded hover:text-white cursor-pointer"
                >
                  ✕
                </button>
              </div>

              <div className="flex items-center gap-2 pb-3 border-b border-white/5 mb-4">
                <Coins className="w-5 h-5 text-emerald-450 animate-pulse" />
                <h3 className="text-base font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-indigo-300 to-[#e840f3] uppercase tracking-widest">
                  ledger allocation gateway
                </h3>
              </div>

              <p className="text-[10px] text-slate-400 leading-relaxed mb-4 font-sans">
                Each query processed through our independent NexoraX channels triggers a standard 1p computational fee. Top up credits immediately below to preserve active prompt routing.
              </p>

              {/* Stripe option */}
              <div className="bg-[#051125]/80 border border-[#e840f3]/15 rounded-xl p-3.5 space-y-3 select-text mb-4">
                <div className="flex items-start gap-2.5">
                  <Landmark className="w-5 h-5 text-indigo-400 shrink-0 mt-0.5" />
                  <div className="flex flex-col gap-1.5 w-full">
                    <span className="text-[10px] font-black uppercase text-indigo-300">AUTHORIZED STRIPE GATEWAY LINK:</span>
                    <a 
                      href="https://buy.stripe.com/8x2bIU2t59oYgTtfxQaMU00" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-[9.5px] text-cyan-400 hover:underline font-bold bg-black/60 p-2.5 rounded border border-cyan-950 flex items-center justify-between group truncate"
                    >
                      <span className="truncate">Invoice Link (Stripe Active Secure)</span>
                      <ArrowUpRight className="w-3.5 h-3.5 text-cyan-400 shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </a>
                  </div>
                </div>
              </div>

              {/* DEMO IMMEDIATE INJECTION CONTROL */}
              <div className="space-y-3 border-t border-white/5 pt-4">
                <label className="text-[9px] text-[#f574fe] font-black uppercase tracking-widest block">INSTANT DEMO FLOODING AMOUNT (PENCE):</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={fundingAmount}
                    onChange={(e) => setFundingAmount(e.target.value)}
                    className="flex-1 bg-black border border-slate-900 rounded-xl px-4 py-2.5 text-[10px] text-emerald-400 font-extrabold focus:outline-none focus:border-emerald-500"
                  />
                  <button
                    onClick={handleFundCredits}
                    className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-500 hover:opacity-90 active:scale-97 text-black font-bold text-[9px] uppercase tracking-widest rounded-xl transition-all cursor-pointer shadow-md shadow-emerald-950/20"
                  >
                    fund ledger
                  </button>
                </div>
                <span className="text-[7.5px] text-slate-500 italic font-medium block text-right">Ledger sync requires 0.8s handshakes.</span>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* DISMISSIBLE INVOICE/MANDATE EXPLANATION PANEL */}
      {showInvoiceGuide && (
        <div className="w-full bg-emerald-950/15 border border-emerald-500/20 rounded-2xl p-4 flex gap-3 text-[10px] relative font-sans leading-relaxed text-slate-300">
          <button 
            type="button" 
            onClick={() => setShowInvoiceGuide(false)}
            className="absolute top-2 right-3 text-[10px] text-slate-500 hover:text-white font-mono cursor-pointer"
          >
            ✕
          </button>
          <div className="flex items-center justify-center shrink-0 h-8 w-8 rounded-xl bg-emerald-950/30 text-emerald-400 border border-emerald-500/10">
            <Info className="w-4 h-4 animate-bounce" />
          </div>
          <div>
            <strong className="text-emerald-400 font-bold uppercase block mb-1">NEXORAX REGISTRY CONFIRMED • DIRECT BALANCE INUNDATED</strong>
            <span>Your instant balance update has been successfully written directly to the memory registers. Ledger balances have been synchronized inside the <strong>Google Drive NexoraX Resource Library</strong> via <strong>CloudSync-02</strong>. Remit actual agency payments and premium licensing quotas via the Stripe Invoice Link above to satisfy full Wednesday obligations.</span>
          </div>
        </div>
      )}

    </div>
  );
};
